import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Crown, 
  Handshake, 
  Check, 
  X, 
  Clock,
  Building2,
  ArrowRight,
  Loader2,
  Zap,
  Eye,
  Shield,
  Sparkles,
  Calendar,
  PartyPopper,
  Undo2,
  AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// SKO 2026 Promo configuration (same as PricingPage)
const SKO_PROMO = {
  eventName: 'SKO 2026',
  discount: 50,
  code: 'SKO2026',
  endDate: new Date('2026-02-28T23:59:59'),
};

const FreelanceChooseModelPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, checkAuth } = useContext(AuthContext);
  
  const [selectedModel, setSelectedModel] = useState(null);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [loading, setLoading] = useState(false);
  const [freelanceStatus, setFreelanceStatus] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  // New states for confirmation modal and undo
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [undoLoading, setUndoLoading] = useState(false);
  
  const wasCancelled = searchParams.get('cancelled') === 'true';

  // Calculate promo time left
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = SKO_PROMO.endDate - now;
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);

  const isPromoActive = new Date() < SKO_PROMO.endDate;

  useEffect(() => {
    fetchFreelanceStatus();
  }, []);

  const fetchFreelanceStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/freelance/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFreelanceStatus(response.data);
      
      if (response.data.freelance_status === 'independent_paid') {
        setSelectedModel('independent_paid');
      } else if (response.data.freelance_status === 'represented_free') {
        setSelectedModel('represented_free');
      }
    } catch (error) {
      console.error('Failed to fetch freelance status:', error);
    }
  };

  const handleChooseModel = async () => {
    if (!selectedModel) {
      toast.error('Please select a model');
      return;
    }

    if (freelanceStatus?.freelance_status === selectedModel) {
      toast.info('You are already on this plan');
      return;
    }

    // Show confirmation modal for Represented Freelance
    if (selectedModel === 'represented_free') {
      setShowConfirmModal(true);
      return;
    }

    // For Independent Paid, proceed directly
    await submitModelChoice();
  };

  const submitModelChoice = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/freelance/choose-model`,
        {
          model: selectedModel,
          billing_cycle: selectedModel === 'independent_paid' ? billingCycle : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.requires_payment) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.success(response.data.message);
        if (checkAuth) await checkAuth();
        await fetchFreelanceStatus(); // Refresh status to show undo option
        setShowConfirmModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process your choice');
    } finally {
      setLoading(false);
    }
  };

  const handleUndoChoice = async () => {
    setUndoLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/freelance/undo-choice`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      if (checkAuth) await checkAuth();
      await fetchFreelanceStatus(); // Refresh status
      setSelectedModel(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo your choice');
    } finally {
      setUndoLoading(false);
    }
  };

  // Prices with SKO discount - Based on official pricing document
  // Normal: €249/month (annual) or €299/month (monthly)
  // After 50% SKO: €125/month (annual) or €150/month (monthly)
  const basePrices = { monthly: 299, annual: 249 };
  const getDiscountedPrice = (price) => {
    if (isPromoActive) {
      return Math.round(price * (1 - SKO_PROMO.discount / 100));
    }
    return price;
  };

  const isInTrial = freelanceStatus?.is_trial && !freelanceStatus?.trial_ended;
  const trialEnded = freelanceStatus?.trial_ended || freelanceStatus?.needs_choice;
  const daysRemaining = freelanceStatus?.days_remaining || 0;
  const currentPlan = freelanceStatus?.freelance_status;
  const hasChosenPlan = currentPlan === 'independent_paid' || currentPlan === 'represented_free';
  const canUndo = freelanceStatus?.can_undo || false;
  const graceHoursRemaining = freelanceStatus?.grace_hours_remaining || 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* SKO 2026 Promo Banner */}
      {isPromoActive && (
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-2.5 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgY3g9IjIwIiBjeT0iMjAiIHI9IjIiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvZz48L3N2Zz4=')] opacity-30"></div>
          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-3 text-center relative">
            <PartyPopper className="w-4 h-4 text-yellow-300 animate-bounce" />
            <span className="font-bold text-sm">
              🎰 {SKO_PROMO.eventName} Special!
            </span>
            <span className="bg-white/20 px-2.5 py-0.5 rounded-full text-xs font-bold">
              {SKO_PROMO.discount}% OFF
            </span>
            <div className="flex items-center gap-1 bg-black/20 px-2.5 py-0.5 rounded-full text-xs">
              <span className="font-mono font-bold">
                {timeLeft.days}d {String(timeLeft.hours).padStart(2, '0')}h {String(timeLeft.minutes).padStart(2, '0')}m
              </span>
              <span className="text-white/70 text-xs ml-1">left</span>
            </div>
            <span className="text-xs text-white/90">
              Code: <span className="font-mono font-bold bg-white/20 px-1.5 py-0.5 rounded">{SKO_PROMO.code}</span>
            </span>
            <Sparkles className="w-4 h-4 text-yellow-300" />
          </div>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-gray-900">
            Pricing & Plans
          </h1>
          <p className="text-sm text-gray-600">
            Choose the plan that fits your freelance activity
          </p>
        </div>

        {/* Trial Status Banner */}
        {isInTrial && (
          <Card className="mb-6 border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-blue-900">
                      14-Day Free Trial
                    </h3>
                    <p className="text-blue-700 text-sm">
                      <span className="font-semibold">{daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining</span> — Full Independent access
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">{daysRemaining}</div>
                  <div className="text-xs text-blue-500">days</div>
                </div>
              </div>
              <div className="mt-3 p-3 bg-white/60 rounded-lg">
                <p className="text-xs text-blue-800">
                  <Sparkles className="w-3 h-3 inline mr-1" />
                  During your trial, enjoy <strong>full access</strong> to all features. Choose your plan before the trial ends.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trial Ended Warning */}
        {trialEnded && !hasChosenPlan && (
          <Card className="mb-6 border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-amber-900">Your Trial Has Ended</h3>
                  <p className="text-amber-700 text-sm">Please select a plan to continue.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Plan Badge */}
        {hasChosenPlan && (
          <Card className={`mb-6 border ${canUndo ? 'border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50' : 'border-green-200 bg-gradient-to-r from-green-50 to-emerald-50'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${canUndo ? 'bg-amber-100' : 'bg-green-100'} flex items-center justify-center`}>
                    {canUndo ? <Clock className="w-5 h-5 text-amber-600" /> : <Check className="w-5 h-5 text-green-600" />}
                  </div>
                  <div>
                    <h3 className={`text-base font-bold ${canUndo ? 'text-amber-900' : 'text-green-900'}`}>
                      Current: {currentPlan === 'independent_paid' ? 'Independent Freelance' : 'Represented Freelance'}
                    </h3>
                    <p className={`text-sm ${canUndo ? 'text-amber-700' : 'text-green-700'}`}>
                      {currentPlan === 'independent_paid' 
                        ? 'Full autonomy and direct access to all features.'
                        : canUndo 
                          ? `You have ${graceHoursRemaining}h left to change your mind.`
                          : 'Represented by SnowExchange Operating Partner.'
                      }
                    </p>
                  </div>
                </div>
                
                {/* Undo Button - Only for represented_free within grace period */}
                {canUndo && currentPlan === 'represented_free' && (
                  <Button
                    variant="outline"
                    onClick={handleUndoChoice}
                    disabled={undoLoading}
                    className="border-amber-400 text-amber-700 hover:bg-amber-100 hover:border-amber-500"
                    data-testid="undo-choice-btn"
                  >
                    {undoLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Undo2 className="w-4 h-4 mr-2" />
                    )}
                    Undo My Choice
                  </Button>
                )}
              </div>
              
              {/* Grace Period Notice */}
              {canUndo && currentPlan === 'represented_free' && (
                <div className="mt-3 p-3 bg-white/60 rounded-lg border border-amber-200">
                  <p className="text-xs text-amber-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>24-Hour Grace Period:</strong> You can revert to the free trial within {graceHoursRemaining} hours. 
                      After that, your choice becomes permanent.
                    </span>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payment Cancelled */}
        {wasCancelled && (
          <Card className="mb-6 border border-amber-300 bg-amber-50">
            <CardContent className="p-3">
              <p className="text-amber-800 font-medium text-sm">Payment cancelled. Please select a plan.</p>
            </CardContent>
          </Card>
        )}

        {/* Billing Toggle - Same style as PM/RM */}
        {(!hasChosenPlan || currentPlan === 'independent_trial') && (
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className={`text-sm font-medium transition-colors ${
              billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-400'
            }`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
              className="relative inline-flex h-7 w-14 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              style={{ backgroundColor: billingCycle === 'annual' ? '#10b981' : '#d1d5db' }}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-300 ${
                billingCycle === 'annual' ? 'translate-x-8' : 'translate-x-1'
              }`} />
            </button>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium transition-colors ${
                billingCycle === 'annual' ? 'text-gray-900' : 'text-gray-400'
              }`}>
                Annual
              </span>
              {billingCycle === 'annual' && (
                <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-emerald-100 text-emerald-700">
                  SAVE 17%
                </span>
              )}
            </div>
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Independent Freelance Plan */}
          <Card 
            className={`relative cursor-pointer transition-all duration-300 hover:shadow-lg border ${
              selectedModel === 'independent_paid' || currentPlan === 'independent_paid'
                ? 'border-emerald-500 shadow-md ring-1 ring-emerald-200' 
                : 'border-slate-200 hover:border-emerald-300'
            }`}
            onClick={() => !hasChosenPlan && setSelectedModel('independent_paid')}
          >
            <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2 z-10">
              <span className="bg-emerald-500 text-white px-3 py-0.5 rounded-full text-xs font-medium shadow">
                Full Autonomy
              </span>
            </div>

            <CardHeader className="pt-6 pb-3">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Crown className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-lg text-slate-900">Independent Freelance</CardTitle>
                  <p className="text-slate-500 text-xs">Starter Plan</p>
                </div>
              </div>

              {/* Pricing */}
              <div className="mt-2">
                {isPromoActive ? (
                  <>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-xl text-slate-400 line-through">
                        €{billingCycle === 'annual' ? basePrices.annual : basePrices.monthly}
                      </span>
                      <span className="text-3xl font-bold text-pink-600">
                        €{getDiscountedPrice(billingCycle === 'annual' ? basePrices.annual : basePrices.monthly)}
                      </span>
                      <span className="text-slate-500 text-sm">/month</span>
                    </div>
                    <p className="text-xs font-bold text-pink-600 mt-1">
                      🎰 SKO 2026 -{SKO_PROMO.discount}% applied!
                    </p>
                  </>
                ) : (
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-slate-900">
                      €{billingCycle === 'annual' ? basePrices.annual : basePrices.monthly}
                    </span>
                    <span className="text-slate-500 text-sm">/month</span>
                  </div>
                )}
                {billingCycle === 'annual' && (
                  <p className="text-xs text-slate-500 mt-1">
                    Billed annually (€{isPromoActive ? getDiscountedPrice(basePrices.annual) * 12 : basePrices.annual * 12}/year)
                  </p>
                )}
              </div>
            </CardHeader>

            <CardContent className="pt-0">
              <p className="text-slate-600 text-sm mb-4">
                Full control. Manage clients, rates, and contracts directly.
              </p>

              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm"><strong>Total autonomy</strong> — You manage your business</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm"><strong>Direct contact</strong> with clients & partners</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm"><strong>0% markup</strong> — Your rate is what clients see</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm"><strong>AI Matching</strong> — Inbound & outbound</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm"><strong>Full messaging</strong> & contract tools</span>
                </li>
              </ul>

              {/* Trial indicator for users currently in trial */}
              {isInTrial && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="font-semibold text-blue-900 text-sm">Your 14-Day Free Trial</span>
                  </div>
                  <p className="text-xs text-blue-700 mt-1">
                    You&apos;re currently enjoying this plan for free. {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining.
                  </p>
                </div>
              )}

              <div className="p-3 bg-emerald-50 rounded-lg">
                <p className="text-xs text-emerald-800">
                  <Zap className="w-3 h-3 inline mr-1" />
                  <strong>Best for:</strong> Freelancers who want full control over client relationships.
                </p>
              </div>

              {currentPlan === 'independent_paid' && (
                <div className="mt-3 p-2 bg-emerald-100 rounded-lg text-center">
                  <span className="text-emerald-800 font-semibold text-sm">✓ Your Current Plan</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Represented Freelance Plan */}
          <Card 
            className={`relative cursor-pointer transition-all duration-300 hover:shadow-lg border ${
              selectedModel === 'represented_free' || currentPlan === 'represented_free'
                ? 'border-purple-500 shadow-md ring-1 ring-purple-200' 
                : 'border-slate-200 hover:border-purple-300'
            }`}
            onClick={() => !hasChosenPlan && setSelectedModel('represented_free')}
          >
            <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2 z-10">
              <span className="bg-purple-500 text-white px-3 py-0.5 rounded-full text-xs font-medium shadow">
                100% Free
              </span>
            </div>

            <CardHeader className="pt-6 pb-3">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Handshake className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg text-slate-900">Represented Freelance</CardTitle>
                  <p className="text-slate-500 text-xs">Free Forever</p>
                </div>
              </div>

              <div className="mt-2">
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-slate-900">€0</span>
                  <span className="text-slate-500 text-sm">/month</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">No subscription, no hidden fees</p>
              </div>
            </CardHeader>

            <CardContent className="pt-0">
              <div className="p-3 bg-purple-50 rounded-lg mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="w-4 h-4 text-purple-600" />
                  <span className="font-semibold text-purple-900 text-sm">Represented by SnowExchange Partner</span>
                </div>
                <p className="text-xs text-purple-700">
                  Your profile is actively marketed by our Operating Partner.
                </p>
              </div>

              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm"><strong>€0 forever</strong> — No subscription</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm"><strong>Full visibility</strong> — Profile shown to all</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm"><strong>AI Matching</strong> — Inbound opportunities</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm"><strong>100% of your rate</strong> — Keep your Daily Rate</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-500 text-sm">No direct commercial contact</span>
                </li>
              </ul>

              <div className="p-3 bg-purple-50 rounded-lg">
                <p className="text-xs text-purple-800">
                  <Eye className="w-3 h-3 inline mr-1" />
                  <strong>Best for:</strong> Freelancers who want visibility without managing sales.
                </p>
              </div>

              {currentPlan === 'represented_free' && (
                <div className="mt-3 p-2 bg-purple-100 rounded-lg text-center">
                  <span className="text-purple-800 font-semibold text-sm">✓ Your Current Plan</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* CTA Button */}
        {!hasChosenPlan && (
          <div className="text-center mb-6">
            <Button
              onClick={handleChooseModel}
              disabled={!selectedModel || loading}
              size="lg"
              className="px-10 py-5 text-base font-semibold"
              style={{ 
                backgroundColor: selectedModel === 'independent_paid' ? '#059669' : 
                                selectedModel === 'represented_free' ? '#7c3aed' : '#94a3b8'
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : isInTrial ? (
                <>
                  {selectedModel === 'independent_paid' ? 'Upgrade to Independent' : 'Switch to Represented'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  {selectedModel === 'independent_paid' ? 'Continue to Payment' : 'Start as Represented'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
            
            <p className="mt-3 text-xs text-slate-500">
              {selectedModel === 'independent_paid' 
                ? 'You will be redirected to Stripe for secure payment.'
                : selectedModel === 'represented_free'
                ? 'By continuing, you agree to be represented by SnowExchange and its Operating Partner.'
                : 'Select a plan above to continue.'
              }
            </p>
          </div>
        )}

        {/* Info Box */}
        <Card className="bg-slate-800">
          <CardContent className="p-5">
            <h3 className="text-base font-semibold mb-2 flex items-center gap-2 text-white">
              <Shield className="w-4 h-4" />
              How the Represented Model Works
            </h3>
            <p className="text-slate-300 text-sm mb-3">
              The Represented plan is <strong className="text-white">100% free</strong> for you. SnowExchange actively markets your profile 
              to clients. You delegate commercial placement; we handle negotiations and contracts.
            </p>
            <ul className="space-y-1.5 text-slate-300 text-xs">
              <li className="flex items-start gap-2">
                <Check className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                <span>You keep <strong className="text-white">100%</strong> of your declared Daily Rate</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                <span>The platform applies a commercial margin on the client side (invisible to you)</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                <span>Focus on your expertise — we handle the business development</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </main>

      {/* Confirmation Modal for Represented Freelance */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-represented-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-900">
              <Handshake className="w-5 h-5 text-purple-600" />
              Confirm Your Choice
            </DialogTitle>
            <DialogDescription className="text-slate-600 pt-2">
              You are about to become a <strong>Represented Freelance</strong> with SnowExchange.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-purple-900 text-sm mb-2">What this means:</h4>
              <ul className="space-y-1.5 text-sm text-purple-800">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span>Your profile will be marketed by our Operating Partner</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span>You keep 100% of your Daily Rate</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">No direct commercial contact with clients</span>
                </li>
              </ul>
            </div>
            
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-amber-900 text-sm">24-Hour Grace Period</h4>
                  <p className="text-xs text-amber-700 mt-1">
                    You can change your mind within 24 hours if you decide this isn't right for you. 
                    After that, your choice becomes permanent.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmModal(false)}
              disabled={loading}
              className="w-full sm:w-auto"
              data-testid="cancel-confirm-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitModelChoice}
              disabled={loading}
              className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-represented-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Handshake className="w-4 h-4 mr-2" />
                  Confirm & Continue
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FreelanceChooseModelPage;
